import dayjs from 'dayjs';

const LENGTH_DATE = 10; // Формат даты в виде 01.01.1972 -> 10 символов
export const FORMAT = 'DD.MM.YYYY';
const MONTHES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];
const WEEKDAYS = [
  'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота', 'воскресенье'
];

export const formatTime = (sec: number) => {
  let seconds: string | number = sec ? sec : 0;
  let minutes: string | number = Math.floor(seconds / 60);

  minutes = minutes >= 10 ? minutes : '0' + minutes;
  seconds = Math.floor(seconds % 60);
  seconds = seconds >= 10 ? seconds : '0' + seconds;

  return minutes + ':' + seconds;
};

export const isDate = (str: string | undefined) => dayjs(str).isValid();

export const isPeriod = (str: string | undefined) => {
  if (!str) return false;

  if (str.includes('&nbsp;-&nbsp;)')) {
    const [ start, end ] = str.split('&nbsp;-&nbsp;');
  
    return dayjs(start, FORMAT).isValid() && dayjs(end, FORMAT).isValid();  
  }

  const length = str.length;
  const start = str.slice(0, LENGTH_DATE);
  const end = str.slice(length - LENGTH_DATE);

  return dayjs(start, FORMAT).isValid() && dayjs(end, FORMAT).isValid();  
};

export const isMonth = (str: string[] | undefined) => {
  if (!str) return false;

  let isValid = false;
  for(let month of MONTHES) {
    if (str.includes(month)) return true;
  }

  return isValid;
};

export const monthSort = (a: string, b: string, typeSort: 'ASC' | 'DESC' | '') => {
  let indexA = 0;
  let indexB = 0;
  let yearA = 0;
  let yearB = 0;

  // Извлекаем индекс месяца
  for(let index in MONTHES) {
    if (a.includes(MONTHES[index])) {
      indexA = Number(index);
    }
    if (b.includes(MONTHES[index])) {
      indexB = Number(index);
    }
  }

  // Извлекаем год из строки (ожидаем формат "Месяц YYYY")
  const yearRegex = /\d{4}/;
  const yearMatchA = a.match(yearRegex);
  const yearMatchB = b.match(yearRegex);
  
  if (yearMatchA) {
    yearA = Number(yearMatchA[0]);
  }
  if (yearMatchB) {
    yearB = Number(yearMatchB[0]);
  }

  // Сначала сортируем по году, затем по месяцу
  if (yearA !== yearB) {
    return typeSort === 'ASC' ? yearA - yearB : yearB - yearA;
  }

  return typeSort === 'ASC' ? indexA - indexB : indexB - indexA;
};

export const isQuart = (str: string | undefined) => !!str && str.toLowerCase().includes('квартал');

export const isWeekday = (str: string | undefined) => {
  if (!str) return false;
  return WEEKDAYS.includes(str.toLowerCase());
};

export const weekdaySort = (a: string, b: string, typeSort: 'ASC' | 'DESC' | '') => {
  const indexA = WEEKDAYS.indexOf(a.toLowerCase());
  const indexB = WEEKDAYS.indexOf(b.toLowerCase());

  return typeSort === 'ASC' ? indexA - indexB : indexB - indexA;
};

/**
 * Проверяет, является ли строка числовым интервалом
 * (например, "1 день", "2 недели", "5 месяцев", "> 15 дней")
 */
export const isNumericInterval = (str: string | undefined) => {
  if (!str || typeof str !== 'string') return false;
  
  const trimmed = str.trim();
  
  // Проверяем наличие числа в начале строки или после ">"
  // Примеры: "1 день", "2 недели", "> 15 дней", "10 мин."
  const numericPattern = /^(>?\s*\d+)\s*(день|дня|дней|неделя|недели|недель|месяц|месяца|месяцев|квартал|квартала|кварталов|год|года|лет|мин\.|минут|час\.|часа|часов|декаминут)/i;
  
  return numericPattern.test(trimmed);
};

/**
 * Извлекает числовое значение из строки с числовым интервалом
 * @param str - строка вида "1 день", "2 недели", "> 15 дней"
 * @returns число или Infinity для значений типа "> 15 дней"
 */
export const extractNumericValue = (str: string | undefined): number => {
  if (!str || typeof str !== 'string') return 0;
  
  const trimmed = str.trim();
  
  // Если строка начинается с ">", это максимальное значение - сортируем в конец
  if (trimmed.startsWith('>')) {
    const match = trimmed.match(/>\s*(\d+)/);
    if (match) {
      // Возвращаем число больше указанного, чтобы "> 15" было после "15"
      return Number(match[1]) + 0.5;
    }
    return Infinity;
  }
  
  // Извлекаем первое число из строки
  const match = trimmed.match(/^(\d+)/);
  if (match) {
    return Number(match[1]);
  }
  
  return 0;
};

/**
 * Сортирует числовые интервалы
 */
export const numericIntervalSort = (a: string, b: string, typeSort: 'ASC' | 'DESC' | '') => {
  const numA = extractNumericValue(a);
  const numB = extractNumericValue(b);
  
  return typeSort === 'ASC' ? numA - numB : numB - numA;
};

/**
 * Правильный порядок сортировки для группировок по периодам от меньшего к большему (А→Я)
 * Каждый массив содержит группу связанных значений в правильном порядке
 */
const PERIOD_SORT_ORDER: string[][] = [
  // От попадания в базу до регистрации
  ['1 день', '2 дня', '3 дня', '4 дня', '5 дней', '6 дней', '7 дней', '8 дней', '9 дней', '10 дней', '11 дней', '12 дней', '13 дней', '14 дней', '15 дней', '> 15 дней'],
  ['День от попадания в базу до регистрации', 'Неделя от попадания в базу до регистрации', 'Месяц от попадания в базу до регистрации', 'Квартал от попадания в базу до регистрации', 'Год от попадания в базу до регистрации'],
  
  // От регистрации до заявки
  ['День от регистрации до заявки', 'Неделя от регистрации до заявки', 'Месяц от регистрации до заявки', 'Квартал от регистрации до заявки', 'Год от регистрации до заявки'],
  
  // От регистрации до заказа
  ['День от регистрации до заказа', 'Дней от регистрации до заказа', 'Неделя от регистрации до заказа', 'Недель от регистрации до заказа', 'Месяц от регистрации до заказа', 'Месяцев от регистрации до заказа', 'Квартал от регистрации до заказа', 'Кварталов от регистрации до заказа', 'Год от регистрации до заказов', 'Лет от регистрации до заказа'],
  
  // От попадания в базу до заказа
  ['День от попадания в базу до заказа', 'Дней от попадания в базу до заказа', 'Неделя от попадания в базу до заказа', 'Недель от попадания в базу до заказа', 'Месяц от попадания в базу до заказа', 'Месяцев от попадания в базу до заказа', 'Квартал от попадания в базу до заказа', 'Кварталов от попадания в базу до заказа', 'Год от попадания в базу до заказов', 'Лет от попадания в базу до заказа'],
  
  // От создания до оплаты заказа
  ['Минут от создания до оплаты заказа', 'Декаминут от создания заказа до оплаты', 'Часов от создания заказа до оплаты', 'Дней от создания заказа до оплаты', 'Недель от создания заказа до оплаты'],
  
  // От регистрации до платежа
  ['День от регистрации до платежа', 'Дней от регистрации до платежа', 'Неделя от регистрации до платежа', 'Недель от регистрации до платежа', 'Месяц от регистрации до платежа', 'Месяцев от регистрации до платежа', 'Квартал от регистрации до платежа', 'Кварталов от регистрации до платежа', 'Год от регистрации до платежа', 'Лет от регистрации до платежа'],
  
  // От попадания в базу до платежа
  ['День от попадания в базу до платежа', 'Дней от попадания в базу до платежа', 'Неделя от попадания в базу до платежа', 'Недель от попадания в базу до платежа', 'Месяц от попадания в базу до платежа', 'Месяцев от попадания в базу до платежа', 'Квартал от попадания в базу до платежа', 'Кварталов от попадания в базу до платежа', 'Год от попадания в базу до платежа', 'Лет от попадания в базу до платежа'],
  
  // От регистрации до вебинара
  ['День от регистрации до вебинара', 'Дней от регистрации до вебинара', 'Неделя от регистрации до вебинара', 'Недель от регистрации до вебинара', 'Месяц от регистрации до вебинара', 'Месяцев от регистрации до вебинара', 'Квартал от регистрации до вебинара', 'Кварталов от регистрации до вебинара', 'Год от регистрации до вебинара', 'Лет от регистрации до вебинара'],
  
  // Дополнительные группировки с числами
  ['1 неделя', '2 недели', '3 недели', '4 недели', '5 недель', '6 недель', '7 недель', '8 недель', '9 недель', '10 недель', '> 10 недель'],
  ['1 месяц', '2 месяца', '3 месяца', '4 месяца', '5 месяцев', '6 месяцев', '7 месяцев', '8 месяцев', '9 месяцев', '10 месяцев', '11 месяцев', '12 месяцев', '> 12 месяцев'],
  ['1 квартал', '2 квартала', '3 квартала', '4 квартала', '> 4 кварталов'],
  ['1 год', '2 года', '3 года', '4 года', '5 лет', '> 5 лет'],
];

/**
 * Создаём Map для быстрого поиска позиции элемента в порядке сортировки
 */
const createSortOrderMap = (): Map<string, number> => {
  const map = new Map<string, number>();
  let globalIndex = 0;
  
  for (const group of PERIOD_SORT_ORDER) {
    for (const item of group) {
      map.set(item.toLowerCase(), globalIndex++);
    }
  }
  
  return map;
};

const SORT_ORDER_MAP = createSortOrderMap();

/**
 * Проверяет, является ли строка группировкой по периодам
 */
export const isPeriodGrouping = (str: string | undefined): boolean => {
  if (!str || typeof str !== 'string') return false;
  return SORT_ORDER_MAP.has(str.trim().toLowerCase());
};

/**
 * Сортирует группировки по периодам используя предопределённый порядок
 */
export const periodGroupingSort = (a: string, b: string, typeSort: 'ASC' | 'DESC' | ''): number => {
  const aLower = a.trim().toLowerCase();
  const bLower = b.trim().toLowerCase();
  
  const indexA = SORT_ORDER_MAP.get(aLower);
  const indexB = SORT_ORDER_MAP.get(bLower);
  
  // Если оба элемента найдены в карте, сортируем по их позиции
  if (indexA !== undefined && indexB !== undefined) {
    return typeSort === 'ASC' ? indexA - indexB : indexB - indexA;
  }
  
  // Если только один найден, он идёт первым при ASC
  if (indexA !== undefined) return typeSort === 'ASC' ? -1 : 1;
  if (indexB !== undefined) return typeSort === 'ASC' ? 1 : -1;
  
  // Если оба не найдены, сортируем алфавитно
  if (typeSort === 'ASC') {
    return aLower < bLower ? -1 : aLower > bLower ? 1 : 0;
  } else {
    return aLower < bLower ? 1 : aLower > bLower ? -1 : 0;
  }
};

/**
 * Приводит МСК-строку "YYYY-MM-DD HH:mm:ss" к виду "дд.мм.гггг чч:мм" (без секунд).
 * Время уже московское — таймзону не применяем, парсим строку как есть.
 * Используется в истории комментариев CRM (школы, пользователи).
 */
export const formatCommentDate = (created_at: string): string => {
  if (!created_at) return '';
  const [ datePart, timePart = '' ] = created_at.split(' ');
  const [ year, month, day ] = datePart.split('-');
  if (!year || !month || !day) return created_at;
  const time = timePart.slice(0, 5); // чч:мм
  return `${day}.${month}.${year}${time ? ` ${time}` : ''}`;
};
